import {
  Box,
  BoxProps,
  Button,
  Container,
  ContainerProps,
  Typography,
} from '@mui/material';
import {ResursLogo} from '@resurs-internal/mui';
import React, {FC} from 'react';
import {useTranslate} from '../../hooks/useTranslate';
import {BackButton} from './BackButton';
import {BaseActionButton, BaseFullPageActions} from './BaseFullPageActions';

export interface BaseFullPageProps extends BoxProps {
  id: Lowercase<string>;
  title?: string;
  header?: React.ReactNode;
  backButton?: {
    title?: string;
    path?: string;
    showCloseIcon?: boolean;
    onClick?: () => void;
    hidden?: boolean;
  };
  actions?: BaseActionButton[];
  // Width of the inner content Container. Defaults to 'sm' — the narrow,
  // single-column treatment every BankID/login/signing screen wants. Pass
  // `false` (no max width) or a wider breakpoint for screens like the
  // document viewer that need the full browser width on desktop.
  containerMaxWidth?: ContainerProps['maxWidth'];
  // Drops the whole top row that holds the back button, not just the button
  // (`backButton.hidden` keeps the row so the layout doesn't shift).
  noTopBar?: boolean;
  // Full-width bar pinned below the content (outside the width-limited
  // container), e.g. the desktop document viewer's confirm button.
  footer?: React.ReactNode;
  // Resurs branding centred in the top bar.
  showLogo?: boolean;
  // Cancel action on the right of the top bar; omit to hide it.
  cancelButton?: {
    title?: string;
    onClick: () => void;
    disabled?: boolean;
  };
}

export const BaseFullPage: FC<BaseFullPageProps> = ({
  id,
  title,
  header,
  backButton,
  children,
  actions,
  sx,
  containerMaxWidth = 'sm',
  noTopBar = false,
  footer,
  showLogo = false,
  cancelButton,
  ...rest
}) => {
  const t = useTranslate();
  return (
    <Box
      id={id}
      component={'main'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Use the visible viewport height (dvh) so the page never grows taller
        // than what's on screen on mobile, where 100vh includes the area behind
        // the browser UI. `overflow: hidden` keeps the page itself from
        // scrolling — only the inner content region scrolls (single scrollbar).
        height: '100vh',
        '@supports (height: 100dvh)': {height: '100dvh'},
        overflow: 'hidden',
        position: 'relative',
        ...sx,
      }}
      {...rest}>
      {header && <Box sx={{flexShrink: 0}}>{header}</Box>}
      {!noTopBar && (
        <Box
          sx={{
            display: 'grid',
            // Back (left), branding (centre), cancel (right). The side
            // columns are equal so the logo stays centred whether or not
            // either side is shown.
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            flexShrink: 0,
            minHeight: {xs: '48px', md: '64px'},
            px: {xs: 2, md: 4},
          }}>
          <Box sx={{justifySelf: 'start'}}>
            {!backButton?.hidden && (
              <BackButton
                path={backButton?.path}
                title={backButton?.title}
                showCloseIcon={backButton?.showCloseIcon}
                navigateBackOnClick={!backButton?.path && !backButton?.onClick}
                onClick={() => {
                  if (backButton?.onClick) backButton.onClick();
                }}
              />
            )}
          </Box>
          <Box>{showLogo && <ResursLogo variant={'text'} width={110} />}</Box>
          <Box sx={{justifySelf: 'end'}}>
            {cancelButton && (
              <Button
                variant={'text'}
                color={'inherit'}
                onClick={cancelButton.onClick}
                disabled={cancelButton.disabled}
                sx={{
                  minWidth: 'auto',
                  p: 0,
                  fontWeight: 'bold',
                  color: 'grey.700',
                }}>
                {cancelButton.title ?? t('common.cancel')}
              </Button>
            )}
          </Box>
        </Box>
      )}
      <Container
        maxWidth={containerMaxWidth}
        sx={{
          flex: 1,
          // Allow this flex child to shrink below its content height so the
          // inner content region (below) is what scrolls, not the page.
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          position: 'relative',
          padding: 0,
          pb: footer ? 0 : 4,
        }}>
        {Boolean(title) && (
          <Typography
            variant={'h2'}
            component={'h1'}
            textAlign={'center'}
            mb={2}>
            {title}
          </Typography>
        )}
        <Box sx={{flex: 1, minHeight: 0, p: 2, overflow: 'auto'}}>
          {children}
        </Box>
        <BaseFullPageActions actions={actions} />
      </Container>
      {footer}
    </Box>
  );
};
